import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';

export interface ManualCurrencyConfig {
    id?: string;
    currency_id: string;
    detail_type: 'bank' | 'crypto' | 'credit_card' | 'custom';
    details: Record<string, any>;
    currency?: { id: string; symbol: string; name?: string };
}

export interface PaymentMethod {
    id: string;
    name: string;
    code: string;
    display_name: string;
    description?: string;
    note?: string;
    requires_address: boolean;
    requires_card_details: boolean;
    requires_bank_details?: boolean;
    requires_holder_name?: boolean;
    method_category?: 'fiat' | 'crypto';
    is_active: boolean;
    method_type?: 'processor' | 'manual';
    supports_processors?: boolean;
    processor_config?: any;
    processing_time_hours: number;
    processing_time_description: string;
    min_amount: number;
    max_amount: number;
    fee_percentage: number;
    fee_fixed: number;
    supported_currencies: any[];
    supported_currency_symbols: string[];
    manual_currency_configs?: ManualCurrencyConfig[];
    metadata?: any;
}

export interface TradingAccountDepositRequest {
    trading_account_id: string;
    amount: number;
    payment_method_id: string;
    payment_details?: Record<string, any>;
    notes?: string;
    /** Required when payment method is manual — must match a configured manual currency */
    deposit_currency_id?: string;
    /** Preferred selector for manual configs (supports duplicate currency rows). */
    deposit_manual_config_id?: string;
}

export interface TradingAccountWithdrawalRequest {
    trading_account_id: string;
    amount: number;
    payment_method_id: string;
    payment_details?: Record<string, any>;
    notes?: string;
    withdrawal_currency_id?: string;
    withdrawal_manual_config_id?: string;
}

// Get available payment methods
export const usePaymentMethods = (currency?: string) => {
    return useQuery<PaymentMethod[]>({
        queryKey: ['payment-methods', currency],
        queryFn: async () => {
            const url = currency
                ? `${API_ENDPOINTS.TRADER.TRANSACTIONS.PAYMENT_METHODS}?currency=${currency}`
                : API_ENDPOINTS.TRADER.TRANSACTIONS.PAYMENT_METHODS;
            const { data } = await httpClient.get<ApiResponse<PaymentMethod[]>>(url);
            return data.data;
        },
    });
};

// Create trading account deposit
export const useCreateTradingAccountDeposit = () => {
    const queryClient = useQueryClient();

    return useMutation<any, Error, TradingAccountDepositRequest>({
        mutationFn: async request => {
            const { data } = await httpClient.post<ApiResponse<any>>(
                API_ENDPOINTS.TRADER.TRANSACTIONS.TRADING_ACCOUNT_DEPOSIT,
                request
            );
            return data.data;
        },
        onSuccess: () => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.TRANSACTIONS.LIST] });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS],
            });

            // Invalidate all balance-related queries
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.ME] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.DETAILS] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.WALLETS] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.HISTORY] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.PNL] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.MARGIN] });
        },
    });
};

// Create trading account withdrawal
export const useCreateTradingAccountWithdrawal = () => {
    const queryClient = useQueryClient();

    return useMutation<any, Error, TradingAccountWithdrawalRequest>({
        mutationFn: async request => {
            const { data } = await httpClient.post<ApiResponse<any>>(
                API_ENDPOINTS.TRADER.TRANSACTIONS.TRADING_ACCOUNT_WITHDRAWAL,
                request
            );
            return data.data;
        },
        onSuccess: () => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.TRANSACTIONS.LIST] });
            queryClient.invalidateQueries({
                queryKey: [API_ENDPOINTS.TRADER.BALANCE.TRADING_ACCOUNTS],
            });

            // Invalidate all balance-related queries
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.ME] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.DETAILS] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.WALLETS] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.HISTORY] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.PNL] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.MARGIN] });
        },
    });
};
